import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Megaphone,
    Contact,
    MessagesSquare,
    ChevronDown,
    KanbanSquare,
    Workflow,
    BarChart3,
    MessageCircle,
    Bot,
    LogOut,
    Radar,
    Image,
    Receipt,
    ClipboardCheck,
} from "lucide-react";

/* ==========================================================
   CONTENT SOURCE
   Sidebar module names are pulled from the PRD "Core Modules"
   list and the 5-screen UI/UX wireframe chapter of the
   GrowthOS AI deck, not invented labels:
   Lead Management, CRM (Customer 360), Marketing Automation,
   Sales Pipeline, Communication Hub, AI Agents, AI Assistant,
   Analytics & BI, Admin & White-label.
========================================================== */

export const navGroups = [

    {
        group: "PRODUCT SCREENS",
        items: [
            { id: "sec-dashboard-preview", label: "Dashboard", icon: LayoutDashboard },
           // { id: "sec-ai-command-center", label: "AI Command Center", icon: Bot },
            { id: "sec-campaign-builder", label: "Lead Management", icon: Megaphone },
            { id: "sec-lead-generation", label: "Lead Generation", icon: Radar },
            { id: "sec-campaign-automation", label: "Campaign Automation", icon: Workflow },
            { id: "sec-sales-pipeline", label: "Sales Pipeline", icon: KanbanSquare },
            { id: "sec-customer-360", label: "Customer 360 View", icon: Contact },
            { id: "sec-media", label: "Media", icon: Image },
            { id: "sec-company_accounting", label: "Accounting", icon: Receipt },
            { id: "sec-approvals", label: "Approvals", icon: ClipboardCheck },
            { id: "sec-communication-hub", label: "Communication Hub", icon: MessageCircle },
            { id: "sec-ai-assistant", label: "AI Assistant", icon: MessagesSquare },
            { id: "sec-analytics-bi", label: "Analytics & BI", icon: BarChart3 },
        ],
    },
];

export const allNavItems = navGroups.flatMap((g) => g.items);

const initials = (name) =>
    (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

// Login.jsx already stores the full company object under this key right
// after a successful login — reading it here is simpler and more
// reliable than a second network round-trip to /auth/me.
const readStoredCompany = () => {
    try {
        const raw = localStorage.getItem("growthos_company");
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        return null;
    }
};

// Team-member session ka apna alag identity, jaise company_Dashboard_sidebar.js
// mein hai. Isko yahan bhi check karna zaroori hai — warna member login pe
// bhi ye sidebar hamesha company/founder ka data hi dikhata rehta, kyunki
// pehle ye sirf growthos_company padhta tha.
const readStoredMember = () => {
    try {
        const raw = localStorage.getItem("growthos_member");
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        return null;
    }
};

// disabled: true while module access is still loading (allowedModuleIds
// === null upstream). Without this, a nav item is clickable the instant
// it renders, but the routed content area is still showing its own
// loading spinner with no <Route>s mounted yet — that first click just
// changes the URL with nothing there to catch it, so it takes 2-3 clicks
// before one lands after access finishes loading. Disabling closes that gap.
const NavItem = ({ item, active, onClick, onInteract, disabled }) => {
    const Icon = item.icon;
    return (
        <button
            disabled={disabled}
            onClick={() => {
                if (disabled) return;
                onInteract?.();
                onClick();
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-[14px] font-semibold transition duration-200 group ${
                disabled
                    ? "text-gray-600 opacity-50 cursor-not-allowed"
                    : active
                    ? "bg-orange-500 text-white"
                    : "text-gray-300 hover:bg-white/5 hover:text-white"
            }`}
        >
            <Icon size={18} className={`shrink-0 ${active ? "text-white" : "text-gray-500 group-hover:text-orange-500"}`} />
            <span className="flex-1 text-left truncate">{item.label}</span>
        </button>
    );
};

// activeItem: id of the currently active section
// goToSection: (id) => void — scrolls to the section and marks it active
// sidebarOpen: boolean — controls collapse/expand animation
// allowedModuleIds: string[] | null — module ids (route segment, no
// "sec-" prefix) this session may open in the CURRENT product, from
// GET /my-module-access. null = still loading (every item stays visible
// but disabled, see NavItem above, so nothing flickers empty on first
// paint). A module NOT in this list is dropped from the sidebar
// entirely — hidden modules should not be visible here at all, only
// blocked from opening.
const Sidebar = ({ activeItem, goToSection, sidebarOpen, allowedModuleIds = null }) => {
    const navigate = useNavigate();
    const [account] = useState(() => readStoredCompany());
    const [member] = useState(() => readStoredMember());
    const [menuOpen, setMenuOpen] = useState(false);

    const isLoadingAccess = allowedModuleIds === null;

    // Filter each group's items down to what's allowed, then drop any
    // group left with nothing in it (only one group exists today, but
    // this keeps working if more are added later).
    const visibleNavGroups = isLoadingAccess
        ? navGroups
        : navGroups
              .map((group) => ({
                  ...group,
                  items: group.items.filter((item) =>
                      allowedModuleIds.includes(item.id.replace(/^sec-/, ""))
                  ),
              }))
              .filter((group) => group.items.length > 0);

    // Scrollbar only shows while the nav is actively being interacted
    // with (scrolled, clicked/dragged) — hidden the rest of the time.
    const [navScrolling, setNavScrolling] = useState(false);
    const scrollTimeoutRef = useRef(null);

    const revealScrollbar = useCallback(() => {
        setNavScrolling(true);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => setNavScrolling(false), 900);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem("growthos_token");
        localStorage.removeItem("growthos_company");
        localStorage.removeItem("growthos_member");
        navigate("/login");
    };

    return (
        <aside
            className={`shrink-0 border-orange-600/20 flex flex-col h-screen sticky top-0 overflow-hidden transition-all duration-300 ease-in-out ${
                sidebarOpen ? "w-72 border-r" : "w-0 border-r-0"
            }`}
        >
            {/* Themed, auto-hiding scrollbar for the nav list only.
                Hidden at rest; fades in (translucent orange, matching
                the accent color) while scrolling or clicking/dragging
                inside the nav, then fades back out shortly after. */}
            <style>{`
                .sidebar-scroll {
                    scrollbar-width: none;
                }
                .sidebar-scroll::-webkit-scrollbar {
                    width: 5px;
                }
                .sidebar-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
                .sidebar-scroll::-webkit-scrollbar-thumb {
                    background-color: transparent;
                    border-radius: 9999px;
                    transition: background-color 0.3s ease;
                }
                .sidebar-scroll.is-scrolling {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(255, 107, 0, 0.35) transparent;
                }
                .sidebar-scroll.is-scrolling::-webkit-scrollbar-thumb {
                    background-color: rgba(255, 107, 0, 0.35);
                }
                .sidebar-scroll.is-scrolling::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(255, 107, 0, 0.55);
                }
            `}</style>

            <div className="w-72 flex flex-col h-full">
                <div className="px-6 py-6 border-b border-orange-600/10">
                    <div className="flex items-center gap-2.5 mb-1">
                        <img
                            src={`${process.env.PUBLIC_URL}/Assests/isf_acedumy_logo.png`}
                            alt="ISF Academy Logo"
                            className="h-9 w-9 object-contain shrink-0"
                        />
                        <h1 className="text-xl font-extrabold text-white leading-none">
                            ISF <span className="text-orange-500">Athena</span>
                        </h1>
                    </div>
                </div>

                <nav
                    className={`sidebar-scroll flex-1 overflow-y-auto px-4 mt-5 space-y-5 pb-4 ${
                        navScrolling ? "is-scrolling" : ""
                    }`}
                    onScroll={revealScrollbar}
                    onMouseDown={revealScrollbar}
                    onTouchStart={revealScrollbar}
                    onClick={revealScrollbar}
                >
                    {visibleNavGroups.map((group) => (
                        <div key={group.group}>
                            <p className="text-[11px] font-bold text-orange-500 tracking-widest px-4 mb-2">
                                {group.group}
                            </p>
                            <div className="space-y-1">
                                {group.items.map((item) => (
                                    <NavItem
                                        key={item.id}
                                        item={item}
                                        active={activeItem === item.id}
                                        onClick={() => goToSection(item.id)}
                                        onInteract={revealScrollbar}
                                        disabled={isLoadingAccess}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className="p-4 border-t border-orange-600/10 relative">
                    {menuOpen && (
                        <>
                            {/* Click-outside catcher */}
                            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                            <div className="absolute z-20 left-4 right-4 bottom-[4.25rem] bg-[#111111] border border-orange-600/20 rounded-xl shadow-lg overflow-hidden">
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-gray-300 hover:bg-white/5 hover:text-red-400 transition"
                                >
                                    <LogOut size={15} />
                                    Logout
                                </button>
                            </div>
                        </>
                    )}

                    <button
                        onClick={() => setMenuOpen((o) => !o)}
                        className="relative z-20 w-full flex items-center gap-3 bg-white/5 hover:bg-white/10 transition rounded-xl px-3 py-2.5"
                    >
                        <div className="h-9 w-9 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-sm shrink-0">
                            {initials(member?.name || account?.company_name)}
                        </div>
                        <div className="text-left flex-1 min-w-0">
                            <p className="text-sm font-semibold text-white leading-none truncate">
                                {member?.name || account?.company_name || "Account"}
                            </p>
                            <p className="text-xs text-gray-500 mt-1 truncate">
                                {member?.email || account?.email || "User"}
                            </p>
                        </div>
                        <ChevronDown
                            size={16}
                            className={`text-gray-500 shrink-0 transition-transform ${menuOpen ? "rotate-180" : ""}`}
                        />
                    </button>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;